import { useEffect, useState } from 'react';
import { X } from '@/components/icons';
import clsx from 'clsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addMasterValue, clearMasterList, deleteMasterValue, fetchMasterList } from '@/api/masters';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';

interface Props {
  listKey: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** allow inline add/delete via API (default true) */
  editable?: boolean;
}

/**
 * Dropdown backed by the `master_list` table on the server. Inline + button
 * adds a new value (POST). The ✕ button opens a manage panel where any option
 * can be individually deleted.
 */
export function SmartSelect({
  listKey,
  value,
  onChange,
  placeholder,
  className,
  disabled,
  editable = true,
}: Props) {
  const toast = useToast();
  const confirmDlg = useConfirm();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [managing, setManaging] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [deletingItem, setDeletingItem] = useState<string | null>(null);

  const { data: values = [] } = useQuery({
    queryKey: ['master-list', listKey],
    queryFn: () => fetchMasterList(listKey),
  });

  // If the current value exists in the item but is not yet in the master list
  // (e.g. imported via Excel), silently register it so it shows up for everyone.
  useEffect(() => {
    if (!value || values.length === 0) return;
    if (values.includes(value)) return;
    addMasterValue(listKey, value).then(() => {
      qc.invalidateQueries({ queryKey: ['master-list', listKey] });
    }).catch(() => { /* ignore — may already exist or be a race */ });
  // Run only once per (listKey + initial values load) — not on every keystroke
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey, values.length]);

  const addMutation = useMutation({
    mutationFn: () => addMasterValue(listKey, newValue.trim()),
    onSuccess: () => {
      const v = newValue.trim();
      qc.invalidateQueries({ queryKey: ['master-list', listKey] });
      setNewValue('');
      setAdding(false);
      onChange(v);
      toast.push(`Added: ${v}`, 'success');
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Could not add value'), 'error'),
  });

  const delMutation = useMutation({
    mutationFn: (item: string) => deleteMasterValue(listKey, item),
    onSuccess: (_data, item) => {
      qc.invalidateQueries({ queryKey: ['master-list', listKey] });
      if (value === item) onChange('');
      setDeletingItem(null);
      toast.push(`Deleted: ${item}`, 'info');
    },
    onError: (e: unknown) => {
      setDeletingItem(null);
      toast.push(apiError(e, 'Could not delete'), 'error');
    },
  });

  const clearAllMutation = useMutation({
    mutationFn: () => clearMasterList(listKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['master-list', listKey] });
      onChange('');
      toast.push('All options deleted', 'info');
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Could not clear'), 'error'),
  });

  const handleDelete = (item: string) => {
    setDeletingItem(item);
    delMutation.mutate(item);
  };

  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      <div className="flex items-center gap-1 sm:gap-1.5">
        <select
          className="w-full rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-2 py-1 text-xs text-[var(--text-primary)] outline-none focus:border-accent disabled:opacity-60 sm:rounded-lg sm:px-3 sm:py-2 sm:text-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">{placeholder ?? '-- Select --'}</option>
          {/* Fallback: show current value even if not yet in the master list */}
          {value && !values.includes(value) && (
            <option key={`__current__${value}`} value={value}>{value}</option>
          )}
          {values.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        {editable && (
          <>
            <button
              type="button"
              onClick={() => { setAdding((x) => !x); setManaging(false); }}
              disabled={disabled}
              className="flex h-7 w-6 flex-shrink-0 items-center justify-center rounded-md border border-accent/30 bg-accent/10 text-sm font-bold text-accent hover:bg-accent/20 disabled:opacity-60 sm:h-9 sm:w-7 sm:text-base"
              title="Add new"
            >
              +
            </button>
            <button
              type="button"
              onClick={() => { setManaging((x) => !x); setAdding(false); }}
              disabled={disabled}
              className="flex h-7 w-6 flex-shrink-0 items-center justify-center rounded-md border border-danger/30 bg-danger/10 text-xs text-danger hover:bg-danger/20 disabled:opacity-40 sm:h-9 sm:w-7 sm:text-sm"
              title="Manage / delete options"
            >
              <X size={13} />
            </button>
          </>
        )}
      </div>

      {/* Add new value row */}
      {adding && (
        <div className="flex gap-1.5">
          <input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (newValue.trim()) addMutation.mutate();
              } else if (e.key === 'Escape') {
                setAdding(false);
                setNewValue('');
              }
            }}
            placeholder="New value (Enter to save, Esc to cancel)"
            autoFocus
            className="w-full rounded-md border border-accent bg-[var(--input-bg)] px-3 py-1.5 text-sm text-[var(--text-primary)] outline-none"
          />
        </div>
      )}

      {/* Manage panel — delete any option */}
      {managing && (
        <div className="rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] shadow-lg">
          <div className="flex items-center justify-between border-b border-[var(--border-color)] px-3 py-2">
            <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide">
              Manage options
            </span>
            <div className="flex items-center gap-2">
              {values.length > 0 && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!await confirmDlg(`Delete ALL ${values.length} options from this list? This cannot be undone.`)) return;
                    clearAllMutation.mutate();
                  }}
                  disabled={clearAllMutation.isPending}
                  className="rounded px-2 py-0.5 text-xs font-semibold text-danger border border-danger/30 hover:bg-danger/10 disabled:opacity-40"
                >
                  {clearAllMutation.isPending ? '…' : 'Delete All'}
                </button>
              )}
              <button
                type="button"
                onClick={() => setManaging(false)}
                className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                Close
              </button>
            </div>
          </div>
          {values.length === 0 && (
            <p className="px-3 py-2 text-xs text-[var(--text-muted)]">No options yet.</p>
          )}
          <ul className="max-h-48 overflow-y-auto py-1">
            {values.map((item) => (
              <li
                key={item}
                className="flex items-center justify-between px-3 py-1.5 hover:bg-[var(--input-bg)] gap-2"
              >
                <span className="text-sm text-[var(--text-primary)] truncate">{item}</span>
                <button
                  type="button"
                  onClick={() => handleDelete(item)}
                  disabled={deletingItem === item}
                  className="shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold text-danger border border-danger/20 hover:bg-danger/10 disabled:opacity-40"
                  title={`Delete "${item}"`}
                >
                  {deletingItem === item ? '…' : 'Delete'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
